import React, { useEffect, useState } from 'react';
import { Table, Button, Tag, Typography, Image, Input, Space, Select, Spin, message, Modal, Form, Radio } from 'antd';
import { SearchOutlined, ReloadOutlined, ClearOutlined, SyncOutlined, DatabaseOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ProductService, Product } from '../services/productService';
import { XMLMappingService, XMLMapping } from '../services/xmlMappingService';
import { getActiveBrandId } from '../config/api';
import XmlMappings from './XmlMappings';

const { Title, Text } = Typography;
const { Option } = Select;

const Products: React.FC = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  // Filters
  const [searchSku, setSearchSku] = useState('');
  const [searchName, setSearchName] = useState('');
  const [searchCategory, setSearchCategory] = useState('');
  const [categories, setCategories] = useState<{ name: string; count: number }[]>([]);

  // Sync Modal
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [mappings, setMappings] = useState<XMLMapping[]>([]);
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [syncForm] = Form.useForm();

  // XML Mapping Modal
  const [xmlMappingModalVisible, setXmlMappingModalVisible] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Get active brand ID based on environment (staging uses stageBrandId, prod uses brandId)
  const activeBrandId = getActiveBrandId(user);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('showMapping') === 'true') {
      setXmlMappingModalVisible(true);
      // Clean up the URL
      searchParams.delete('showMapping');
      navigate({ search: searchParams.toString() }, { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    if (activeBrandId) {
      loadProducts();
      loadCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBrandId, currentPage, pageSize]);

  const loadProducts = async () => {
    if (!activeBrandId) {
      message.warning('Brand ID bulunamadı');
      return;
    }

    try {
      setLoading(true);
      const offset = (currentPage - 1) * pageSize;
      
      const response = await ProductService.getProducts({
        brandId: activeBrandId,
        limit: pageSize,
        offset,
        sku: searchSku || undefined,
        name: searchName || undefined,
        category: searchCategory || undefined,
      });

      if (response.success) {
        setProducts(response.data.products);
        setTotal(response.data.pagination.total);
      } else {
        message.error('Ürünler yüklenemedi');
      }
    } catch (error) {
      console.error('Failed to load products:', error);
      message.error('Ürünler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    if (!activeBrandId) return;

    try {
      const cats = await ProductService.getCategories(activeBrandId);
      setCategories(cats);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page
    loadProducts();
  };

  const handleClearFilters = () => {
    setSearchSku('');
    setSearchName('');
    setSearchCategory('');
    setCurrentPage(1);
    setTimeout(() => loadProducts(), 100);
  };

  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleOpenSyncModal = async () => {
    if (!activeBrandId) {
      message.warning('Brand ID bulunamadı');
      return;
    }

    setSyncModalVisible(true);
    setLoadingMappings(true);

    try {
      const mappingsList = await XMLMappingService.getMappingsByBrand(activeBrandId, true);
      setMappings(mappingsList);

      if (mappingsList.length === 0) {
        message.warning('Aktif mapping bulunamadı. Lütfen önce bir mapping oluşturun.');
      } else if (mappingsList.length === 1) {
        // Tek mapping varsa otomatik seç
        syncForm.setFieldsValue({ mappingName: mappingsList[0].mappingName });
      }
    } catch (error) {
      message.error('Mappingler yüklenemedi');
    } finally {
      setLoadingMappings(false);
    }
  };

  const handleSyncProducts = async (values: any) => {
    if (!activeBrandId) {
      message.warning('Brand ID bulunamadı');
      return;
    }

    try {
      setSyncing(true);
      const { mappingName, limitProducts } = values;

      message.loading({ content: 'Ürünler güncelleniyor...', key: 'sync', duration: 0 });

      const result = await ProductService.syncProductsFromBrand(
        activeBrandId,
        mappingName,
        limitProducts,
        false,
        limitProducts === 10000 // 10000 seçilirse tüm ürünleri al
      );

      message.destroy('sync');

      if (result.success && result.data.syncResult.success) {
        const syncResult = result.data.syncResult;
        message.success({
          content: `${syncResult.totalSynced} ürün başarıyla güncellendi! (${syncResult.totalCreated} yeni, ${syncResult.totalUpdated} güncellendi)`,
          duration: 5,
        });
        setSyncModalVisible(false);
        syncForm.resetFields();
        loadProducts(); // Ürünleri yeniden yükle
      } else {
        message.error('Ürün güncelleme başarısız: ' + (result.data.syncResult.errors.join(', ') || 'Bilinmeyen hata'));
      }
    } catch (error: any) {
      message.destroy('sync');
      message.error(error.message || 'Ürün güncelleme sırasında hata oluştu');
    } finally {
      setSyncing(false);
    }
  };

  const handleCloseSyncModal = () => {
    setSyncModalVisible(false);
    syncForm.resetFields();
  };

  const columns = [
    {
      title: 'Görsel',
      dataIndex: 'images',
      key: 'images',
      width: 100,
      render: (url: string, record: Product) => <Image src={record.images[0]} width={60} height={60} />,
    },
    {
      title: 'Ürün Adı',
      dataIndex: 'title',
      key: 'title',
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: 'Kategori',
      dataIndex: 'category',
      key: 'category',
    },
    {
      title: 'Fiyat',
      dataIndex: 'price',
      key: 'price',
      render: (price: number, record: Product) => (
        <Text strong>{price} {record.currency}</Text>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Aktif' : 'Pasif'}
        </Tag>
      ),
    },
  ];

  if (!activeBrandId) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">Brand ID bekleniyor...</Text>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2}>Ürünlerim</Title>
          <Text type="secondary">Markanıza ait tüm ürünler ({total} ürün)</Text>
        </div>
        <Space>
          <Button
            icon={<DatabaseOutlined />}
            onClick={() => setXmlMappingModalVisible(true)}
            size="large"
          >
            Eşleştirmeleri Göster
          </Button>
          <Button
            type="primary"
            icon={<SyncOutlined />}
            onClick={handleOpenSyncModal}
            size="large"
          >
            Ürünlerimi Güncelle
          </Button>
        </Space>
      </div>

      {/* Filters */}
      <div style={{ marginBottom: 16, background: '#fafafa', padding: 16, borderRadius: 8 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap>
            <Input
              placeholder="SKU ile ara..."
              prefix={<SearchOutlined />}
              style={{ width: 200 }}
              value={searchSku}
              onChange={(e) => setSearchSku(e.target.value)}
              onPressEnter={handleSearch}
            />
            <Input
              placeholder="Ürün adı ile ara..."
              prefix={<SearchOutlined />}
              style={{ width: 200 }}
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              onPressEnter={handleSearch}
            />
            <Select
              placeholder="Kategori seçin..."
              style={{ width: 200 }}
              value={searchCategory || undefined}
              onChange={(value) => setSearchCategory(value)}
              allowClear
            >
              {categories.map((cat) => (
                <Option key={cat.name} value={cat.name}>
                  {cat.name} ({cat.count})
                </Option>
              ))}
            </Select>
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
              Ara
            </Button>
            <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
              Temizle
            </Button>
            <Button icon={<ReloadOutlined />} onClick={loadProducts}>
              Yenile
            </Button>
          </Space>
          {(searchSku || searchName || searchCategory) && (
            <div>
              <Text type="secondary">Aktif Filtreler: </Text>
              {searchSku && <Tag closable onClose={() => { setSearchSku(''); handleSearch(); }}>SKU: {searchSku}</Tag>}
              {searchName && <Tag closable onClose={() => { setSearchName(''); handleSearch(); }}>Ad: {searchName}</Tag>}
              {searchCategory && <Tag closable onClose={() => { setSearchCategory(''); handleSearch(); }}>Kategori: {searchCategory}</Tag>}
            </div>
          )}
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={products}
        rowKey="id"
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showTotal: (total) => `Toplam ${total} ürün`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        onChange={handleTableChange}
      />

      {/* Sync Modal */}
      <Modal
        title="Ürünleri Güncelle"
        open={syncModalVisible}
        onCancel={handleCloseSyncModal}
        footer={null}
        width={500}
      >
        <Spin spinning={loadingMappings}>
          <Form
            form={syncForm}
            layout="vertical"
            onFinish={handleSyncProducts}
            initialValues={{
              limitProducts: 100,
            }}
          >
            <Form.Item
              name="mappingName"
              label="Mapping Seçimi"
              rules={[{ required: true, message: 'Lütfen bir mapping seçin' }]}
            >
              <Select
                placeholder="Mapping seçin..."
                disabled={mappings.length === 0}
              >
                {mappings.map((mapping) => (
                  <Option key={mapping.id} value={mapping.mappingName}>
                    {mapping.mappingName}
                    {mapping.isActive && <Tag color="green" style={{ marginLeft: 8 }}>Aktif</Tag>}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="limitProducts"
              label="Ürün Limiti"
              rules={[{ required: true, message: 'Lütfen bir limit seçin' }]}
            >
              <Radio.Group>
                <Space direction="vertical">
                  <Radio value={100}>
                    <Space>
                      <span>100 Ürün</span>
                      <Text type="secondary" style={{ fontSize: 12 }}>(Test için)</Text>
                    </Space>
                  </Radio>
                  <Radio value={1000}>
                    <Space>
                      <span>1,000 Ürün</span>
                      <Text type="secondary" style={{ fontSize: 12 }}>(Orta ölçek)</Text>
                    </Space>
                  </Radio>
                  <Radio value={10000}>
                    <Space>
                      <span>10,000 Ürün</span>
                      <Text type="secondary" style={{ fontSize: 12 }}>(Tüm ürünler)</Text>
                    </Space>
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SyncOutlined />}
                  loading={syncing}
                  disabled={mappings.length === 0}
                >
                  Güncelle
                </Button>
                <Button onClick={handleCloseSyncModal} disabled={syncing}>
                  İptal
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* XML Mapping Modal */}
      <Modal
        title={
          <div>
            <Title level={4} style={{ margin: 0 }}>XML Mapping</Title>
            <Text type="secondary">XML Field eşleştirmelerinizi görüntüleyin</Text>
          </div>
        }
        open={xmlMappingModalVisible}
        onCancel={() => setXmlMappingModalVisible(false)}
        footer={null}
        width={1000}
        destroyOnClose
      >
        <XmlMappings isComponent={true} />
      </Modal>
    </div>
  );
};

export default Products;

